import { api, unwrap } from "@lumberset/api-client";
import { ago, dateTime, money } from "@lumberset/takeoff-ui";
import {
  Badge,
  Button,
  Container,
  cn,
  Drawer,
  DropdownMenu,
  EmptyState,
  Field,
  IconButton,
  Input,
  RadioGroup,
  SectionHeader,
  Select,
  Skeleton,
  StatusBadge,
  Table,
  Tabs,
  Textarea,
  type Tone,
  Tooltip,
  toast,
} from "@lumberset/ui";
import {
  CurrencyDollar,
  EllipsisHorizontal,
  ExclamationCircle,
  History,
  InformationCircle,
  MagnifyingGlass,
  PencilSquare,
  Plus,
  ReceiptPercent,
} from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { Crumbs } from "../../components/crumbs";
import {
  type BookPrice,
  categoryLabels,
  itemCheckQuery,
  type MyProposal,
  myPendingQuery,
  myProposalsQuery,
  type PriceCategory,
  type PriceHistory,
  type PriceSource,
  type PriceUnit,
  priceBookQuery,
  priceCategories,
  priceHistoryQuery,
  priceUnits,
  sourceLabels,
  unitLabels,
} from "../../lib/queries-prices";

export const Route = createFileRoute("/_app/prices")({
  validateSearch: (search: Record<string, unknown>): { tab?: "mine" } =>
    search.tab === "mine" ? { tab: "mine" } : {},
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(priceBookQuery({})),
  component: Prices,
});

const STARTER_NOTE =
  "Starter estimates came with LumberSet and are not any supplier's price. Replace them with real prices as you get them.";

/* ---- Small helpers ---------------------------------------------------------- */

/** "12.50" is 1250. Blank, negative or unreadable text is null. */
function dollarsToCents(text: string): number | null {
  const cleaned = text.trim().replace(/[$,\s]/g, "");
  if (!cleaned) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : null;
}

const centsToDollars = (cents: number) => (cents / 100).toFixed(2);

/** An "as of" date is a day, stored as that day's midnight in UTC, so it is shown in UTC too. */
const dayFormat = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeZone: "UTC" });
const day = (value: string | null | undefined) => (value ? dayFormat.format(new Date(value)) : "-");

function today(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function percentChange(before: number, after: number): number {
  return before === 0 ? 0 : Math.round(((after - before) / before) * 1000) / 10;
}

const signed = (percent: number) => `${percent > 0 ? "+" : ""}${percent}%`;

const categoryLabel = (category: string) => categoryLabels[category as PriceCategory] ?? category;

const sourceTones: Record<PriceSource, Tone> = {
  starter_estimate: "orange",
  supplier_quote: "green",
  import: "blue",
  staff_proposal: "purple",
  admin: "grey",
  market_adjustment: "purple",
};

function SourceBadge({ source, note }: { source: string; note?: string | null }) {
  const badge = (
    <Badge color={sourceTones[source as PriceSource] ?? "grey"} className="whitespace-nowrap">
      {sourceLabels[source as PriceSource] ?? source}
    </Badge>
  );
  return note ? (
    <Tooltip content={note}>
      <span className="inline-flex">{badge}</span>
    </Tooltip>
  ) : (
    badge
  );
}

const statusLooks: Record<string, { label: string; tone: Tone }> = {
  pending: { label: "Waiting for an Admin", tone: "orange" },
  approved: { label: "Approved", tone: "green" },
  rejected: { label: "Rejected", tone: "red" },
  withdrawn: { label: "Withdrawn", tone: "grey" },
};

function StatusTag({ status }: { status: string }) {
  const look = statusLooks[status] ?? { label: status, tone: "grey" as const };
  return <StatusBadge color={look.tone}>{look.label}</StatusBadge>;
}

const kindLabels: Record<string, string> = {
  new_item: "New item",
  price_update: "New price",
  edit: "Better description",
  market_adjustment: "Market move",
  import: "Price sheet",
};

function Warnings({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="txt-small flex items-start gap-x-2 rounded-lg bg-ui-tag-orange-bg px-3 py-2 text-ui-tag-orange-text">
      <ExclamationCircle className="mt-0.5 shrink-0" />
      <ul className="flex flex-col gap-y-1">
        {warnings.map((warning) => (
          <li key={warning}>{warning}</li>
        ))}
      </ul>
    </div>
  );
}

/** A value that settles a moment after the person stops typing. */
function useSettled<Value>(value: Value, ms = 350): Value {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/** How the matcher reads a description as it is typed. */
function useItemCheck(input: { description: string; unit: PriceUnit; code?: string }) {
  const description = useSettled(input.description.trim());
  const code = useSettled(input.code?.trim() ?? "");
  const { data } = useQuery(itemCheckQuery({ description, unit: input.unit, code }));
  return description ? data : undefined;
}

function MatchReading({ check }: { check: ReturnType<typeof useItemCheck> }) {
  if (!check) return null;
  return (
    <div className="flex flex-col gap-y-2">
      <p className="txt-small text-ui-fg-subtle">
        The matcher reads this as{" "}
        <span
          className={cn(
            "txt-compact-small-plus",
            check.strength === "strong" ? "text-ui-fg-base" : "text-ui-tag-orange-text",
          )}
        >
          {check.recognisedAs}
        </span>
        .
      </p>
      <Warnings warnings={check.warnings} />
    </div>
  );
}

/* ---- The screen ------------------------------------------------------------ */

type Proposing =
  | { kind: "new_item" }
  | { kind: "price_update"; item: BookPrice }
  | { kind: "edit"; item: BookPrice }
  | null;

/**
 * The reference price book for Staff: browse it, propose new items and new
 * prices, and follow what an Admin decided. Nothing here deletes a price.
 */
function Prices() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: pending } = useQuery(myPendingQuery);
  const [proposing, setProposing] = useState<Proposing>(null);
  const show = (next: "book" | "mine") =>
    navigate({ search: next === "mine" ? { tab: "mine" } : {}, replace: true });

  return (
    <>
      <Crumbs trail={[{ label: "Reference prices" }]} />
      <Tabs
        value={tab ?? "book"}
        onValueChange={(value) => show(value as "book" | "mine")}
        className="flex flex-col gap-y-3"
      >
        <Container className="divide-y p-0">
          <SectionHeader
            level="h1"
            title="Reference prices"
            description="The prices behind Builders' Budget Estimates. Propose new items and better prices with where they come from; an Admin approves them. Nothing you do here deletes a price."
            actions={
              <Button size="small" onClick={() => setProposing({ kind: "new_item" })}>
                <Plus /> Propose a new item
              </Button>
            }
          />
          <div className="px-6 py-3">
            <Tabs.List>
              <Tabs.Trigger value="book">Price book</Tabs.Trigger>
              <Tabs.Trigger value="mine">
                My proposals
                {pending && pending.pending > 0 ? (
                  <Badge color="orange" rounded="full">
                    {pending.pending}
                  </Badge>
                ) : null}
              </Tabs.Trigger>
            </Tabs.List>
          </div>
        </Container>

        <Tabs.Content value="book" className="flex flex-col gap-y-3">
          <BookTab onPropose={setProposing} />
        </Tabs.Content>
        <Tabs.Content value="mine" className="flex flex-col gap-y-3">
          <MineTab onPropose={() => setProposing({ kind: "new_item" })} />
        </Tabs.Content>
      </Tabs>

      <ProposeDrawer
        proposing={proposing}
        onClose={() => setProposing(null)}
        onSent={() => {
          setProposing(null);
          show("mine");
        }}
      />
    </>
  );
}

function BookTab({ onPropose }: { onPropose: (proposing: Proposing) => void }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<PriceCategory | undefined>();
  const q = useSettled(search, 250);
  const { data, isPending } = useQuery(priceBookQuery({ q, category }));
  const [historyId, setHistoryId] = useState<string | null>(null);
  const prices = data?.prices ?? [];
  const summary = data?.summary;

  return (
    <>
      <Container className="divide-y p-0">
        {summary && summary.starterEstimates > 0 ? (
          <div className="txt-small flex items-start gap-x-2 bg-ui-tag-orange-bg px-6 py-3 text-ui-tag-orange-text">
            <InformationCircle className="mt-0.5 shrink-0" />
            <p>
              {STARTER_NOTE}{" "}
              <span className="font-medium">
                {summary.starterEstimates} of {summary.active} are still starter estimates.
              </span>
            </p>
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-2 px-6 py-3 sm:grid-cols-[1fr_200px] lg:max-w-2xl">
          <Input
            type="search"
            aria-label="Search codes and descriptions"
            placeholder="Search codes and descriptions"
            prefix={<MagnifyingGlass />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select
            value={category ?? "all"}
            onValueChange={(value) =>
              setCategory(value === "all" ? undefined : (value as PriceCategory))
            }
          >
            <Select.Trigger aria-label="Kind of material">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value="all">Every kind</Select.Item>
              {priceCategories.map((value) => (
                <Select.Item key={value} value={value}>
                  {categoryLabels[value]}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </div>

        {isPending ? (
          <div className="flex flex-col gap-y-3 px-6 py-4">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
          </div>
        ) : prices.length === 0 ? (
          <EmptyState
            icon={<CurrencyDollar />}
            title="Nothing matches"
            description="Try another search. If the item is not in the book, propose it."
            action={
              <Button
                variant="secondary"
                size="small"
                onClick={() => onPropose({ kind: "new_item" })}
              >
                <Plus /> Propose a new item
              </Button>
            }
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Item</Table.HeaderCell>
                <Table.HeaderCell className="text-right max-sm:hidden">Price</Table.HeaderCell>
                <Table.HeaderCell className="max-md:hidden">As of</Table.HeaderCell>
                <Table.HeaderCell className="max-md:hidden">Where from</Table.HeaderCell>
                <Table.HeaderCell>
                  <span className="sr-only">Actions</span>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {prices.map((price) => (
                <Table.Row
                  key={price.id}
                  data-clickable="true"
                  onClick={() => setHistoryId(price.id)}
                  className={cn(
                    price.source === "starter_estimate" &&
                      "bg-ui-tag-orange-bg/30 hover:bg-ui-tag-orange-bg/50",
                  )}
                >
                  <Table.Cell className="py-2.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="code-label whitespace-nowrap text-ui-fg-base">
                        {price.code}
                      </span>
                      {price.region !== "US" ? <Badge>{price.region}</Badge> : null}
                      {price.pendingProposals > 0 ? (
                        <Badge color="blue" className="whitespace-nowrap">
                          {price.pendingProposals} waiting
                        </Badge>
                      ) : null}
                    </div>
                    <div className="txt-compact-small max-w-[240px] truncate text-ui-fg-subtle sm:max-w-[300px]">
                      {price.description}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 md:hidden">
                      <span className="tabular txt-compact-small whitespace-nowrap sm:hidden">
                        <span className="text-ui-fg-base">{money(price.priceCents)}</span>
                        <span className="text-ui-fg-muted"> / {price.unit}</span>
                      </span>
                      <SourceBadge source={price.source} note={price.sourceNote} />
                      <span className="txt-compact-xsmall text-ui-fg-muted">
                        as of {day(price.priceAsOf)}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="tabular whitespace-nowrap text-right max-sm:hidden">
                    <span className="text-ui-fg-base">{money(price.priceCents)}</span>
                    <span className="text-ui-fg-muted"> / {price.unit}</span>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-md:hidden">
                    {day(price.priceAsOf)}
                  </Table.Cell>
                  <Table.Cell className="py-2.5 max-md:hidden">
                    <SourceBadge source={price.source} note={price.sourceNote} />
                    {price.sourceNote ? (
                      <div
                        className="txt-compact-xsmall max-w-[220px] truncate text-ui-fg-muted"
                        title={price.sourceNote}
                      >
                        {price.sourceNote}
                      </div>
                    ) : null}
                  </Table.Cell>
                  {/* The menu acts on the row without also opening its history. */}
                  <Table.Cell className="w-[1%]" onClick={(event) => event.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenu.Trigger asChild>
                        <IconButton
                          size="small"
                          variant="transparent"
                          aria-label={`Actions for ${price.code}`}
                        >
                          <EllipsisHorizontal />
                        </IconButton>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content align="end">
                        <DropdownMenu.Item
                          onSelect={() => onPropose({ kind: "price_update", item: price })}
                        >
                          <ReceiptPercent /> Propose a new price
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                          onSelect={() => onPropose({ kind: "edit", item: price })}
                        >
                          <PencilSquare /> Suggest a better description
                        </DropdownMenu.Item>
                        <DropdownMenu.Item onSelect={() => setHistoryId(price.id)}>
                          <History /> History
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </DropdownMenu>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <HistoryDrawer
        id={historyId}
        onClose={() => setHistoryId(null)}
        onPropose={(item) => {
          setHistoryId(null);
          const row = prices.find((price) => price.id === item.id);
          if (row) onPropose({ kind: "price_update", item: row });
        }}
      />
    </>
  );
}

/* ---- Proposing ---------------------------------------------------------------- */

function ProposeDrawer({
  proposing,
  onClose,
  onSent,
}: {
  proposing: Proposing;
  onClose: () => void;
  onSent: () => void;
}) {
  return (
    <Drawer open={proposing !== null} onOpenChange={(open) => !open && onClose()}>
      <Drawer.Content>
        {proposing?.kind === "new_item" ? (
          <NewItemForm onClose={onClose} onSent={onSent} />
        ) : proposing?.kind === "price_update" ? (
          <PriceForm
            key={proposing.item.id}
            item={proposing.item}
            onClose={onClose}
            onSent={onSent}
          />
        ) : proposing?.kind === "edit" ? (
          <EditForm
            key={proposing.item.id}
            item={proposing.item}
            onClose={onClose}
            onSent={onSent}
          />
        ) : null}
      </Drawer.Content>
    </Drawer>
  );
}

type Proposal = Parameters<typeof api.staff.prices.proposals.$post>[0]["json"];

/** Sends a proposal, then says what an Admin will look at twice. */
function useSend(onSent: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (json: Proposal) => unwrap(api.staff.prices.proposals.$post({ json })),
    onSuccess: async ({ proposal, warnings }) => {
      await queryClient.invalidateQueries({ queryKey: ["prices"] });
      toast.success(`${proposal.code} sent to an Admin`, {
        description:
          warnings.length > 0
            ? `An Admin will see this too: ${warnings.join(" ")}`
            : "You will see here when it is approved.",
      });
      onSent();
    },
    onError: (error) => toast.error(error.message),
  });
}

type ProposalSource = "supplier_quote" | "staff_proposal";

/** "Where does this price come from?", required for every price Staff propose. */
function SourceFields({
  note,
  onNote,
  kind,
  onKind,
  asOf,
  onAsOf,
  error,
}: {
  note: string;
  onNote: (value: string) => void;
  kind: ProposalSource;
  onKind: (value: ProposalSource) => void;
  asOf: string;
  onAsOf: (value: string) => void;
  error: string | null;
}) {
  return (
    <div className="flex flex-col gap-y-5 rounded-lg border border-ui-border-base p-4">
      <Field
        label="Where does this price come from?"
        hint="A supplier and quote number, a price sheet, a web page: whatever backs it up."
        error={error}
      >
        {(control) => (
          <Input
            {...control}
            value={note}
            onChange={(event) => onNote(event.target.value)}
            placeholder="Parr Lumber quote 44120"
            maxLength={500}
          />
        )}
      </Field>
      <RadioGroup
        value={kind}
        onValueChange={(value) => onKind(value as ProposalSource)}
        className="grid-cols-1 sm:grid-cols-2"
      >
        <RadioGroup.ChoiceBox
          value="supplier_quote"
          label="A supplier's price"
          description="A quote or price sheet from a yard."
        />
        <RadioGroup.ChoiceBox
          value="staff_proposal"
          label="My own research"
          description="A web price, a catalog or your judgment."
        />
      </RadioGroup>
      <Field label="Price as of" hint="The day the price applies. Today unless it is older.">
        {(control) => (
          <Input
            {...control}
            type="date"
            max={today()}
            value={asOf}
            onChange={(event) => onAsOf(event.target.value)}
          />
        )}
      </Field>
    </div>
  );
}

function ItemSummary({ item }: { item: BookPrice }) {
  return (
    <div className="flex flex-col gap-y-1 rounded-lg bg-ui-bg-subtle px-4 py-3">
      <span className="code-label text-ui-fg-base">{item.code}</span>
      <span className="txt-small text-ui-fg-subtle">{item.description}</span>
      <span className="txt-small flex flex-wrap items-center gap-2 text-ui-fg-subtle">
        <span className="tabular text-ui-fg-base">
          {money(item.priceCents)} per {item.unit}
        </span>
        <SourceBadge source={item.source} note={item.sourceNote} />
        <span className="text-ui-fg-muted">as of {day(item.priceAsOf)}</span>
      </span>
    </div>
  );
}

function NewItemForm({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const [code, setCode] = useState("");
  const [region, setRegion] = useState("US");
  const [description, setDescription] = useState("");
  const [unit, setUnit] = useState<PriceUnit>("ea");
  const [category, setCategory] = useState<PriceCategory | "">("");
  const [priceText, setPriceText] = useState("");
  const [sourceNote, setSourceNote] = useState("");
  const [sourceKind, setSourceKind] = useState<ProposalSource>("staff_proposal");
  const [asOf, setAsOf] = useState(today());
  const [note, setNote] = useState("");
  const [tried, setTried] = useState(false);
  const check = useItemCheck({ description, unit, code });
  const chosenCategory = (category || check?.category || "other") as PriceCategory;
  const cents = dollarsToCents(priceText);
  const send = useSend(onSent);

  const problems = {
    code: !code.trim() ? "Give the item a code, such as 2X6-18." : null,
    description: !description.trim() ? "Describe the item in yard shorthand." : null,
    price: cents === null || cents <= 0 ? "A price above zero, such as 14.25." : null,
    sourceNote: sourceNote.trim().length < 3 ? "Say where this price comes from." : null,
  };
  const show = (problem: string | null) => (tried ? problem : null);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTried(true);
    if (Object.values(problems).some(Boolean) || check?.codeTaken) return;
    send.mutate({
      kind: "new_item",
      code: code.trim(),
      region: region.trim() || "US",
      description: description.trim(),
      unit,
      category: chosenCategory,
      priceCents: cents ?? 0,
      source: sourceKind,
      sourceNote: sourceNote.trim(),
      effectiveAt: asOf || undefined,
      note: note.trim() || undefined,
    });
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>Propose a new item</Drawer.Title>
        <Drawer.Description>
          An Admin approves it before Budget Estimates use it. It is recorded as yours.
        </Drawer.Description>
      </Drawer.Header>
      <Drawer.Body className="flex flex-col gap-y-5">
        <div className="grid grid-cols-[1fr_96px] gap-3">
          <Field
            label="Code"
            error={
              show(problems.code) ??
              (check?.codeTaken
                ? `${check.codeTaken} is already in the book. Propose a new price for it instead.`
                : null)
            }
          >
            {(control) => (
              <Input
                {...control}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="2X6-18"
                autoComplete="off"
              />
            )}
          </Field>
          <Field label="Region">
            {(control) => (
              <Input
                {...control}
                value={region}
                onChange={(event) => setRegion(event.target.value)}
              />
            )}
          </Field>
        </div>
        <Field
          label="Description"
          hint="Write it the way a yard does, so the matcher can read it: 2x6x18 DF #2, 7/16 in OSB 4x8, Simpson LUS26."
          error={show(problems.description)}
        >
          {(control) => (
            <Input
              {...control}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={300}
            />
          )}
        </Field>
        <MatchReading check={check} />
        <div className="grid grid-cols-2 gap-3">
          <UnitSelect value={unit} onChange={setUnit} />
          <CategorySelect value={category || chosenCategory} onChange={setCategory} />
        </div>
        <Field label="Price" hint={`Per ${unit}.`} error={show(problems.price)}>
          {(control) => (
            <Input
              {...control}
              inputMode="decimal"
              prefix="$"
              value={priceText}
              onChange={(event) => setPriceText(event.target.value)}
              className="tabular"
            />
          )}
        </Field>
        <SourceFields
          note={sourceNote}
          onNote={setSourceNote}
          kind={sourceKind}
          onKind={setSourceKind}
          asOf={asOf}
          onAsOf={setAsOf}
          error={show(problems.sourceNote)}
        />
        <NoteField value={note} onChange={setNote} />
      </Drawer.Body>
      <FormFooter onClose={onClose} sending={send.isPending} />
    </form>
  );
}

function PriceForm({
  item,
  onClose,
  onSent,
}: {
  item: BookPrice;
  onClose: () => void;
  onSent: () => void;
}) {
  const [priceText, setPriceText] = useState(centsToDollars(item.priceCents));
  const [sourceNote, setSourceNote] = useState("");
  const [sourceKind, setSourceKind] = useState<ProposalSource>("staff_proposal");
  const [asOf, setAsOf] = useState(today());
  const [note, setNote] = useState("");
  const [tried, setTried] = useState(false);
  const cents = dollarsToCents(priceText);
  const send = useSend(onSent);
  const change = cents === null ? 0 : percentChange(item.priceCents, cents);

  const problems = {
    price: cents === null || cents <= 0 ? "A price above zero, such as 14.25." : null,
    sourceNote: sourceNote.trim().length < 3 ? "Say where this price comes from." : null,
  };
  const show = (problem: string | null) => (tried ? problem : null);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTried(true);
    if (Object.values(problems).some(Boolean)) return;
    send.mutate({
      kind: "price_update",
      referencePriceId: item.id,
      priceCents: cents ?? 0,
      source: sourceKind,
      sourceNote: sourceNote.trim(),
      effectiveAt: asOf || undefined,
      note: note.trim() || undefined,
    });
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>Propose a new price</Drawer.Title>
        <Drawer.Description>
          An Admin approves it before Budget Estimates use it. The price it replaces stays in the
          history.
        </Drawer.Description>
      </Drawer.Header>
      <Drawer.Body className="flex flex-col gap-y-5">
        <ItemSummary item={item} />
        <Field
          label="New price"
          hint={
            cents !== null && cents !== item.priceCents
              ? `${signed(change)} on ${money(item.priceCents)}, per ${item.unit}.`
              : `Per ${item.unit}. The same price with a real source confirms it is still right.`
          }
          error={show(problems.price)}
        >
          {(control) => (
            <Input
              {...control}
              inputMode="decimal"
              prefix="$"
              value={priceText}
              onChange={(event) => setPriceText(event.target.value)}
              className="tabular"
            />
          )}
        </Field>
        {Math.abs(change) >= 50 ? (
          <Warnings
            warnings={[
              `${Math.abs(change)}% ${change > 0 ? "above" : "below"} the current price. Check the unit (${item.unit}) and the source.`,
            ]}
          />
        ) : null}
        <SourceFields
          note={sourceNote}
          onNote={setSourceNote}
          kind={sourceKind}
          onKind={setSourceKind}
          asOf={asOf}
          onAsOf={setAsOf}
          error={show(problems.sourceNote)}
        />
        <NoteField value={note} onChange={setNote} />
      </Drawer.Body>
      <FormFooter onClose={onClose} sending={send.isPending} />
    </form>
  );
}

function EditForm({
  item,
  onClose,
  onSent,
}: {
  item: BookPrice;
  onClose: () => void;
  onSent: () => void;
}) {
  const [description, setDescription] = useState(item.description);
  const [unit, setUnit] = useState<PriceUnit>(item.unit as PriceUnit);
  const [category, setCategory] = useState<PriceCategory>(item.category as PriceCategory);
  const [note, setNote] = useState("");
  const check = useItemCheck({ description, unit });
  const send = useSend(onSent);
  const changed =
    description.trim() !== item.description || unit !== item.unit || category !== item.category;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!changed || !description.trim()) return;
    send.mutate({
      kind: "edit",
      referencePriceId: item.id,
      ...(description.trim() !== item.description ? { description: description.trim() } : {}),
      ...(unit !== item.unit ? { unit } : {}),
      ...(category !== item.category ? { category } : {}),
      note: note.trim() || undefined,
    });
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>Suggest a better description</Drawer.Title>
        <Drawer.Description>
          For wording the matcher misreads, or the wrong unit or kind. Prices change through a new
          price.
        </Drawer.Description>
      </Drawer.Header>
      <Drawer.Body className="flex flex-col gap-y-5">
        <ItemSummary item={item} />
        <Field label="Description">
          {(control) => (
            <Input
              {...control}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={300}
            />
          )}
        </Field>
        <MatchReading check={check} />
        <div className="grid grid-cols-2 gap-3">
          <UnitSelect value={unit} onChange={setUnit} />
          <CategorySelect value={category} onChange={setCategory} />
        </div>
        <NoteField value={note} onChange={setNote} />
      </Drawer.Body>
      <FormFooter onClose={onClose} sending={send.isPending} disabled={!changed} />
    </form>
  );
}

function UnitSelect({
  value,
  onChange,
}: {
  value: PriceUnit;
  onChange: (unit: PriceUnit) => void;
}) {
  return (
    <Field label="Sold by">
      {(control) => (
        <Select value={value} onValueChange={(next) => onChange(next as PriceUnit)}>
          <Select.Trigger id={control.id}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            {priceUnits.map((unit) => (
              <Select.Item key={unit} value={unit}>
                {unitLabels[unit]}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      )}
    </Field>
  );
}

function CategorySelect({
  value,
  onChange,
}: {
  value: PriceCategory;
  onChange: (category: PriceCategory) => void;
}) {
  return (
    <Field label="Kind of material">
      {(control) => (
        <Select value={value} onValueChange={(next) => onChange(next as PriceCategory)}>
          <Select.Trigger id={control.id}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            {priceCategories.map((category) => (
              <Select.Item key={category} value={category}>
                {categoryLabels[category]}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      )}
    </Field>
  );
}

function NoteField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <Field label="Note for the Admin" optional hint="Why this item or price, in a sentence.">
      {(control) => (
        <Textarea
          {...control}
          rows={2}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          maxLength={1000}
        />
      )}
    </Field>
  );
}

function FormFooter({
  onClose,
  sending,
  disabled,
}: {
  onClose: () => void;
  sending: boolean;
  disabled?: boolean;
}) {
  return (
    <Drawer.Footer>
      <Button type="button" variant="secondary" size="small" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" size="small" isLoading={sending} disabled={disabled}>
        Send for approval
      </Button>
    </Drawer.Footer>
  );
}

/* ---- My proposals ------------------------------------------------------------- */

function ProposalLine({ proposal }: { proposal: MyProposal }) {
  if (proposal.kind === "edit") {
    return <p className="txt-small text-ui-fg-subtle">{proposal.description}</p>;
  }
  const before = proposal.kind === "price_update" ? proposal.previousPriceCents : null;
  return (
    <p className="txt-small text-ui-fg-subtle">
      {before !== null && before !== proposal.priceCents ? (
        <>
          <span className="tabular">{money(before)}</span> to{" "}
        </>
      ) : null}
      <span className="tabular text-ui-fg-base">{money(proposal.priceCents)}</span> per{" "}
      {proposal.unit}
      {before !== null && before !== proposal.priceCents
        ? ` (${signed(percentChange(before, proposal.priceCents))})`
        : ""}
      {proposal.kind === "new_item" ? `, ${proposal.description}` : ""}
    </p>
  );
}

function MineTab({ onPropose }: { onPropose: () => void }) {
  const queryClient = useQueryClient();
  const { data, isPending } = useQuery(myProposalsQuery);
  const proposals = data?.proposals ?? [];

  const withdraw = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.staff.prices.proposals[":id"].withdraw.$post({ param: { id } })),
    onSuccess: async ({ proposal }) => {
      await queryClient.invalidateQueries({ queryKey: ["prices"] });
      toast.success(`${proposal.code} withdrawn`);
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="My proposals"
        description="What you proposed, newest first, and what an Admin decided. You can withdraw one while it waits."
      />
      {isPending ? (
        <div className="flex flex-col gap-y-3 px-6 py-4">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-5 w-1/2" />
        </div>
      ) : proposals.length === 0 ? (
        <EmptyState
          icon={<ReceiptPercent />}
          title="Nothing proposed yet"
          description="Propose a new item, or a new price from the price book. An Admin approves it before Budget Estimates use it."
          action={
            <Button variant="secondary" size="small" onClick={onPropose}>
              <Plus /> Propose a new item
            </Button>
          }
        />
      ) : (
        proposals.map((proposal) => (
          <div
            key={proposal.id}
            className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="flex min-w-0 flex-col gap-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="txt-compact-small-plus text-ui-fg-base">
                  {kindLabels[proposal.kind] ?? proposal.kind}
                </span>
                <span className="code-label">{proposal.code}</span>
                <StatusTag status={proposal.status} />
              </div>
              <ProposalLine proposal={proposal} />
              {proposal.kind !== "edit" ? (
                <div className="flex flex-wrap items-center gap-2">
                  <SourceBadge source={proposal.source} />
                  <span className="txt-small text-ui-fg-subtle">{proposal.sourceNote}</span>
                </div>
              ) : null}
              <p className="txt-compact-xsmall text-ui-fg-muted">
                Sent {ago(proposal.createdAt)}
                {proposal.status !== "pending" &&
                proposal.status !== "withdrawn" &&
                proposal.reviewedBy
                  ? `. ${proposal.status === "approved" ? "Approved" : "Rejected"} by ${proposal.reviewedBy.name}, ${dateTime(proposal.reviewedAt)}`
                  : ""}
              </p>
              {proposal.reviewNote && proposal.status !== "withdrawn" ? (
                <p className="txt-small rounded-lg bg-ui-bg-subtle px-3 py-2 text-ui-fg-subtle">
                  <span className="txt-compact-small-plus text-ui-fg-base">
                    {proposal.reviewedBy?.name ?? "The Admin"}:
                  </span>{" "}
                  {proposal.reviewNote}
                </p>
              ) : null}
              {proposal.status === "pending" ? <Warnings warnings={proposal.warnings} /> : null}
            </div>
            {proposal.status === "pending" ? (
              <Button
                variant="secondary"
                size="small"
                className="shrink-0"
                isLoading={withdraw.isPending && withdraw.variables === proposal.id}
                onClick={() => withdraw.mutate(proposal.id)}
              >
                Withdraw
              </Button>
            ) : null}
          </div>
        ))
      )}
    </Container>
  );
}

/* ---- History ------------------------------------------------------------------- */

type HistoryItem = PriceHistory["item"];

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-x-3 py-1.5">
      <dt className="txt-compact-small text-ui-fg-muted">{label}</dt>
      <dd className="txt-compact-small min-w-0 break-words text-ui-fg-base">{children}</dd>
    </div>
  );
}

/** Where one price came from and everything that happened to it, newest first. */
function HistoryDrawer({
  id,
  onClose,
  onPropose,
}: {
  id: string | null;
  onClose: () => void;
  onPropose: (item: HistoryItem) => void;
}) {
  const { data, error } = useQuery({ ...priceHistoryQuery(id ?? ""), enabled: id !== null });
  const shown = data && data.item.id === id ? data : undefined;
  const fromBook =
    shown && !shown.item.createdBy && !shown.changes.some((change) => change.kind === "new_item");

  return (
    <Drawer open={id !== null} onOpenChange={(open) => !open && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>{shown ? shown.item.code : "Price history"}</Drawer.Title>
          <Drawer.Description>
            {shown ? shown.item.description : "Every change to this price, newest first."}
          </Drawer.Description>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-5">
          {shown ? (
            <>
              <dl className="divide-y divide-ui-border-base rounded-lg border border-ui-border-base px-3">
                <Fact label="Price">
                  <span className="tabular">{money(shown.item.priceCents)}</span>{" "}
                  <span className="text-ui-fg-muted">per {shown.item.unit}</span>
                </Fact>
                <Fact label="Where from">
                  <span className="flex flex-col items-start gap-y-1">
                    <SourceBadge source={shown.item.source} />
                    {shown.item.sourceNote ? (
                      <span className="text-ui-fg-subtle">{shown.item.sourceNote}</span>
                    ) : null}
                  </span>
                </Fact>
                <Fact label="As of">{day(shown.item.priceAsOf)}</Fact>
                <Fact label="Kind">{categoryLabel(shown.item.category)}</Fact>
                <Fact label="Matcher reads">{shown.item.recognisedAs}</Fact>
                <Fact label="Added by">
                  {shown.item.createdBy?.name ?? "LumberSet starter book"}
                </Fact>
              </dl>
              <ul>
                {shown.changes.map((change) => (
                  <li
                    key={change.id}
                    className="flex flex-col gap-y-1 border-b border-ui-border-base py-3 last:border-b-0"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="txt-compact-small-plus text-ui-fg-base">
                        {kindLabels[change.kind] ?? change.kind}
                      </span>
                      {change.status !== "approved" ? <StatusTag status={change.status} /> : null}
                    </div>
                    {change.kind !== "edit" ? (
                      <p className="txt-small text-ui-fg-subtle">
                        {change.before?.priceCents !== undefined &&
                        change.before.priceCents !== change.priceCents ? (
                          <>
                            <span className="tabular">{money(change.before.priceCents)}</span> to{" "}
                          </>
                        ) : null}
                        <span className="tabular text-ui-fg-base">{money(change.priceCents)}</span>{" "}
                        per {change.unit}
                      </p>
                    ) : (
                      <p className="txt-small text-ui-fg-subtle">
                        {change.note ?? change.description}
                      </p>
                    )}
                    {change.kind !== "edit" && change.sourceNote ? (
                      <p className="txt-small text-ui-fg-subtle">
                        <SourceBadge source={change.source} /> {change.sourceNote}
                      </p>
                    ) : null}
                    <p className="txt-compact-xsmall text-ui-fg-muted">
                      {change.proposedBy?.name ?? "Someone no longer here"},{" "}
                      {dateTime(change.createdAt)}
                      {change.reviewedBy &&
                      change.reviewedBy.id !== change.proposedBy?.id &&
                      change.status !== "pending"
                        ? `. ${change.status === "approved" ? "Approved" : "Rejected"} by ${change.reviewedBy.name}`
                        : ""}
                    </p>
                  </li>
                ))}
                {fromBook ? (
                  <li className="flex flex-col gap-y-1 py-3">
                    <span className="txt-compact-small-plus text-ui-fg-base">
                      Came with LumberSet
                    </span>
                    <p className="txt-small text-ui-fg-subtle">
                      Part of the starter book: our estimate from typical US retail rates, not a
                      supplier's price.
                    </p>
                  </li>
                ) : null}
              </ul>
            </>
          ) : error ? (
            <EmptyState title="We could not load this history" description={error.message} />
          ) : (
            <Skeleton className="h-40 w-full" />
          )}
        </Drawer.Body>
        {shown ? (
          <Drawer.Footer>
            <Button size="small" onClick={() => onPropose(shown.item)}>
              <ReceiptPercent /> Propose a new price
            </Button>
          </Drawer.Footer>
        ) : null}
      </Drawer.Content>
    </Drawer>
  );
}
